import React, { useEffect, useMemo, useState } from 'react'
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, FormLabel, RadioGroup,
    FormControlLabel, Radio, Checkbox,
    FormGroup, Select, MenuItem, InputLabel, Box, Chip,
    IconButton,
    ListItemText,
    Typography
} from '@mui/material'
import { Autocomplete } from '@mui/material'
import FormControl from '@mui/material/FormControl';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TextField from '@mui/material/TextField';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import EastIcon from "@mui/icons-material/East";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getReview } from '../../redux/slices/reviewerSlice';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import VendorSearchBar from '../projectUser/vendors/VendorSearchBar';
import { STATUS } from '../../utils/status';
import debounce from 'lodash.debounce';
import { getVendorAction } from '../../redux/slices/vendor';
import ClearIcon from '@mui/icons-material/Clear';
import { getConfigForTable, getConfigsData } from '../../redux/slices/config';
import AddIcon from '@mui/icons-material/Add';
import { unwrapResult } from '@reduxjs/toolkit';
import { constants } from '../../utils/constants';


// Types for your API data
type Config = {
    config_details: any; id: string; name: string, type: 'TASK' | 'REVIEW', stage_id?: any
}
type Stage = {
    category: string;
    stage_type: string; id: string; name: string
}
type Vendor = { id: string; name: string }

enum TaskStatus {
    Active = 'Active',
    NotStarted = 'Not Started',
    Completed = 'Completed',
    QualityRejected = 'Quality Rejected',
}

enum ReviewStatus {
    Active = 'Active',
    NotStarted = 'Not Started',
    AwaitingTaskUpdate = 'Awaiting Task Update',
    QualityReject = 'Quality Reject',
    AvailableToReview = 'Available to Review',
    Completed = 'Completed',
}

interface Props {
    open: boolean
    onClose: () => void
    projectId: string
    configs: Config[]
    stages: Stage[]
    vendors: Vendor[]
    onSubmit: (values: ExportFormValues) => void
}

export interface ExportFormValues {
    format: 'csv' | 'excel'
    delimiter?: string
    stageId: string    // which config‐stages
    includeReviewerData: boolean
    statusStageId: string   // for status dropdown
    statusId: string[]     // statuses
    rangeStageId: string
    from: Dayjs | null
    to: Dayjs | null
    expertData: boolean
    inputTaskData: boolean
    configDataOption: 'user_selected' | 'output_value'
}

type Row = {
    stageId: string
    configIds: string[]
    includeReviewerData: boolean
    configOptions: Config[]
    name?: string
}

const emptyRow: Row = {
    stageId: '',
    configIds: [],
    includeReviewerData: false,
    configOptions: [],
}

const ExportCompletedTasksDialog: React.FC<Props> = ({ open, onClose, configs, stages, vendors, onSubmit }) => {
    const [exportType, setExportType] = useState<'complete' | 'custom'>('complete')
    const [customStages, setCustomStages] = useState<any[]>([])
    const [selectedStageIds, setSelectedStageIds] = useState<number[]>([])
    const [includeInputData, setIncludeInputData] = useState(false)
    const [format, setFormat] = useState<'csv' | 'excel'>('csv')
    const [delimiter, setDelimiter] = useState(',')
    const [stageId, setStageId] = useState<string>('')
    const [includeReviewerData, setIncludeReviewerData] = useState(false)
    const [statusStageId, setStatusStageId] = useState('')
    const [status, setStatus] = useState<string[]>([])
    const [rangeStageId, setRangeStageId] = useState('')
    const [from, setFrom] = useState<Dayjs | null>(null)
    const [to, setTo] = useState<Dayjs | null>(null)
    const [expertData, setExpertData] = useState(false)
    const [inputTaskData, setInputTaskData] = useState(false)
    const [configDataOption, setConfigDataOption] = useState<'user_selected' | 'output_value'>('user_selected')
    const [vendorSearchOpen, setVendorSearchOpen] = useState(false);
    const [vendorId, setVendorId] = useState("");
    const [selectedVendor, setSelectedVendor] = useState<any | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [options, setOptions] = useState<any[]>([]);
    const [configId, setConfigId] = useState<string>("");
    const [rows, setRows] = useState<Row[]>([{ ...emptyRow }])
    const [uniqueTypes, setUniqueTypes] = useState<string[]>([])
    const association_type = useSelector(
        (state: RootState) => state.projects.association_type
      );

    const reviewStages = useSelector((state: RootState) => state.reviewStage.reviewer?.results || []);
    const Vendors = useSelector((state: RootState) => state.vendors.vendors)
    const getVendorStatus = useSelector((state: RootState) => state.vendors.getVendorsStatus)
    const config = useSelector((state: RootState) => state.config.config);

    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();



    const initial = {
        format: 'csv' as 'csv' | 'excel',
        delimiter: ',',
        stageId: '',
        includeReviewerData: false,
        statusStageId: '',
        statusId: [] as string[],
        rangeStageId: '',
        from: null as Dayjs | null,
        to: null as Dayjs | null,
        expertData: false,
        inputTaskData: false,
        configDataOption: 'user_selected' as 'user_selected' | 'output_value',
        vendorSearchOpen: false,
        vendorId: '',
        selectedVendor: null as any,
        searchTerm: '',
        options: [] as any[],
        configId: '',
        rows: [{ ...emptyRow }],
    };

    const resetAll = () => {
        setExportType('complete');
        setCustomStages([]);
        setSelectedStageIds([]);
        setIncludeInputData(false);
        setFormat(initial.format);
        setDelimiter(initial.delimiter);
        setStageId(initial.stageId);
        setIncludeReviewerData(initial.includeReviewerData);
        setStatusStageId(initial.statusStageId);
        setStatus(initial.statusId);
        setRangeStageId(initial.rangeStageId);
        setFrom(initial.from);
        setTo(initial.to);
        setExpertData(initial.expertData);
        setInputTaskData(initial.inputTaskData);
        setConfigDataOption(initial.configDataOption);
        setVendorSearchOpen(initial.vendorSearchOpen);
        setVendorId(initial.vendorId);
        setSelectedVendor(initial.selectedVendor);
        setSearchTerm(initial.searchTerm);
        setOptions(initial.options);
        setConfigId(initial.configId);
        setRows(initial.rows);
    };


    const debouncedSearch = useMemo(() => debounce((q: string) => dispatch(getVendorAction({ search_query: q })), 300), [dispatch]);
    const handleVendorSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const q = e.target.value.trim();
        setSearchTerm(q);
        debouncedSearch(q);
    };
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    useEffect(() => {
        dispatch(getReview({ project_id: id || "", association_type: "COMPANY" }));
      }, [dispatch, id, association_type]);

    // Fetch stages for custom export when dialog opens
    useEffect(() => {
        if (open && id) {
            console.log("HArsh : ", constants.API_BASE_URL)
            fetch(`${constants.API_BASE_URL}project/company/stage/?project_id=${id}`)
                .then(res => res.json())
                .then(data => {
                    if (data.results) {
                        setCustomStages(data.results);
                    }
                })
                .catch(err => console.error('Failed to fetch stages:', err));
        }
    }, [open, id]);

    const handleSubmit = async () => {
        if (exportType === 'complete') {
            // Complete mode - use existing flow
            const payload = {
                project_id: id || '',
                format,
                ...(format === 'csv' && { delimiter }),
                config_data_option: configDataOption,
                include_expert_data: expertData,
                include_task_input_data: inputTaskData,
                stages: rows.map(r => ({
                    stage_id: r.stageId,
                    config_ids: r.configIds,
                    include_reviewer_data: r.includeReviewerData,
                })),
                status_stage_id: statusStageId,
                status: status,
                range_stage_id: rangeStageId,
                from: from?.toISOString() ?? null,
                to: to?.toISOString() ?? null,
                vendor_id: vendorId,
            };

            const res = await dispatch(getConfigsData(payload));
        } else {
            // Custom mode - use new flow with stages_list and input_data_required
            const customPayload = {
                project_id: id || '',
                stages_list: selectedStageIds,
                input_data_required: includeInputData,
            };

            // Call the data-dump API directly
            try {
                const response = await fetch(
                    `${constants.API_BASE_URL}task/company/data-dump/?project_id=${id}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(customPayload),
                    }
                );
                
                if (response.ok) {
                    // Handle successful download
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `export_${new Date().getTime()}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                }
            } catch (error) {
                console.error('Export failed:', error);
            }
        }
    }

    const currentStage = reviewStages.find((s: any) => s.id === statusStageId);
    const targetRow = rows.find(r => String(r.stageId) === statusStageId);

    const useReview = targetRow?.configOptions.some(c => c.type === 'REVIEW');

    const stageOptions = useMemo(() => {
        const taskEntry = { id: 'TASK', name: 'Task', type: 'TASK' };
        return [taskEntry, ...reviewStages];
    }, [reviewStages]);

    const handleStageChange = (idx: number, newStageId: string) => {
        setStageId(newStageId)
        updateRow(idx, {
            stageId: newStageId,
            configIds: [],
            configOptions: [],
        })

        dispatch(getConfigForTable({
            project_id: id || '',
            ...(newStageId !== 'TASK'
                ? { stage_id: newStageId, type: 'REVIEW' }
                : { type: 'TASK' }),
        }))
            .then(unwrapResult)
            .then(data => {
                updateRow(idx, { configOptions: data.results })
            })
            .catch(err => {
                console.error('Config fetch failed for row', idx, err)
            })
    }

    const handleClose = () => {
        resetAll();
        onClose();
    };
    const handleStatusStageChange = (e: any) => {
        setStatusStageId(e)

    }
    const statusOptions: string[] = statusStageId === "Task" ? Object.values(TaskStatus) : Object.values(ReviewStatus);

    // useEffect(() => {
    //     if (Array.isArray(rows) && rows.length > 0) {
    //         const allTypes = rows.flatMap(row => row.configOptions.map(c => c.type));
    //         const distinct = Array.from(new Set(allTypes));
    //         setUniqueTypes(distinct);
    //     }
    // }, []);

    useEffect(() => {
        if (!open) return;
        const validConfigs = rows.filter(row => row.configOptions.length > 0);
        if (validConfigs.length === 0) return;

        const allTypes = validConfigs.flatMap(row => row.configOptions.map(c => c.type));
        const distinct = Array.from(new Set(allTypes));
        setUniqueTypes(distinct);
    }, [open]);


    const updateRow = (idx: number, patch: Partial<Row>) => setRows(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r))
    const addRow = () => setRows(rs => [...rs, { ...emptyRow }])
    const removeRow = (idx: number) => { setRows(rs => rs.filter((_, i) => i !== idx)) }


    return (
        <Dialog open={open}
            // onClose={onClose} 
            fullWidth maxWidth="md">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: "20px" }}>
                <DialogTitle>Export Completed Tasks</DialogTitle>
                <ClearIcon onClick={handleClose} style={{ cursor: "pointer" }} />
            </div>
            <DialogContent dividers>
                {/* Export Type Selection */}
                <FormControl component="fieldset" fullWidth sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, fontFamily: "montserrat" }}>Export Type :</FormLabel>
                    <RadioGroup value={exportType} onChange={(e: any) => setExportType(e.target.value)}>
                        <FormControlLabel 
                            value="complete" 
                            control={<Radio />} 
                            label={<span>Complete</span>} 
                        />
                        <FormControlLabel 
                            value="custom" 
                            control={<Radio />} 
                            label={<span>Custom (Stages)</span>} 
                        />
                    </RadioGroup>
                </FormControl>

                {/* Custom Stages Selection */}
                {exportType === 'custom' && (
                    <Box component="fieldset" sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, fontFamily: "montserrat" }}>Select Stages :</FormLabel>
                        <FormGroup>
                            {customStages.map((stage) => (
                                <FormControlLabel
                                    key={stage.id}
                                    control={
                                        <Checkbox
                                            checked={selectedStageIds.includes(stage.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedStageIds([...selectedStageIds, stage.id]);
                                                } else {
                                                    setSelectedStageIds(selectedStageIds.filter(id => id !== stage.id));
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>{stage.name}</span>
                                            <Chip
                                                size="small"
                                                sx={{
                                                    fontSize: "11px",
                                                    backgroundColor: stage.category === "REVIEW" ? "#1976d2" : "#f0f0f0",
                                                    color: stage.category === "REVIEW" ? "#ffffff" : "#000000",
                                                    border: "1px solid #ccc"
                                                }}
                                                label={stage.category || "Task"}
                                            />
                                        </Box>
                                    }
                                />
                            ))}
                        </FormGroup>
                        <FormControlLabel
                            sx={{ mt: 2 }}
                            control={
                                <Checkbox
                                    checked={includeInputData}
                                    onChange={(e) => setIncludeInputData(e.target.checked)}
                                />
                            }
                            label={<span>Include Input Data?</span>}
                        />
                    </Box>
                )}

                {/* Existing fields - only show in Complete mode */}
                {exportType === 'complete' && (
                    <>
                <Box component="fieldset" sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormLabel component="legend" sx={{ fontWeight: 600, fontFamily: 'montserrat' }}>
                        Stage Rows:
                    </FormLabel>
                    {rows?.map((row, i) => {
                        return (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <FormControl size="small" sx={{ width: '28%' }}>

                                    <Autocomplete
                                        size="small"
                                        options={stageOptions}
                                        getOptionLabel={opt => opt.name}
                                        value={stageOptions.find(o => o.id === row.stageId) || null}
                                        onChange={(_, newVal) => { handleStageChange(i, newVal?.id ?? '') }}
                                        renderInput={params => (<TextField {...params} label="Select stages" />)}
                                        renderOption={(props, stageOption) => {
                                            return (
                                                <Box
                                                    component="li"
                                                    {...props}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                        px: 1,
                                                        gap: "10px",
                                                        alignSelf: "center"
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>

                                                        <Typography variant="body2" noWrap>
                                                            {stageOption.name}
                                                        </Typography>

                                                        <Chip
                                                            size="small"
                                                            sx={{
                                                                fontSize: "11px",
                                                                backgroundColor: stageOption.stage_type === "REVIEW" ? "#1976d2" : "default",
                                                                color: stageOption.stage_type === "REVIEW" ? "#ffffff" : "#000000",
                                                                border: "1px solid #ccc"
                                                            }}
                                                            label={stageOption.category || "Task"}
                                                        />
                                                    </Box>
                                                </Box>
                                            )
                                        }}

                                    />





                                </FormControl>

                                <FormControl size="small" sx={{ width: '20%' }}>
                                    <InputLabel id={`config-label-${i}`}>Configs</InputLabel>
                                    <Select
                                        labelId={`config-label-${i}`}
                                        multiple
                                        value={row.configIds}
                                        onChange={e => updateRow(i, { configIds: e.target.value as string[] })}
                                        renderValue={selectedIds => selectedIds.map(id => row.configOptions.find(c => c.id === id)?.name).filter(Boolean).join(', ')}
                                        label="Configs"
                                    >
                                        {row.configOptions.map(c => (
                                            <MenuItem key={c.id} value={c.id}>
                                                <Checkbox checked={row.configIds.includes(c.id)} size="small" />
                                                <ListItemText primary={c.name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small">
                                    <FormControlLabel
                                        control={<Checkbox size='small' checked={row.includeReviewerData} onChange={e => updateRow(i, { includeReviewerData: e.target.checked })} />}
                                        label="Include Reviewer Edited Data"
                                    />
                                </FormControl>

                                {i > 0 && (
                                    <IconButton color="error" onClick={() => removeRow(i)} size="small">
                                        <ClearIcon />
                                    </IconButton>
                                )}

                                {i === rows.length - 1 && (
                                    <IconButton color="primary" onClick={addRow} size="small">
                                        <AddIcon />
                                    </IconButton>
                                )}
                            </Box>
                        )
                    })}
                </Box>

                <Box component="fieldset" sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1, display: "flex", gap: "20px" }}>
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, fontFamily: "montserrat" }}>Status :</FormLabel>
                    <FormControl fullWidth size='small'>
                        <InputLabel id="status-stage-label">Select stages</InputLabel>
                        <Select
                            size='small'
                            labelId="status-stage-label"
                            id="status-stage-select"
                            value={statusStageId}
                            label="Stage for Status"
                            onChange={(e: any) => handleStatusStageChange(e.target.value)}
                            // onChange={(e) => setStatusStageId(e.target.value)}
                            fullWidth
                        >
                            <MenuItem value="Task">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <Typography variant="body2">Task</Typography>
                                    <Chip
                                        size="small"
                                        sx={{
                                            fontSize: "11px",
                                            backgroundColor: "#f0f0f0",
                                            color: "#000000",
                                            border: "1px solid #ccc",
                                        }}
                                        label="Task"
                                    />
                                </Box>
                            </MenuItem>
                            {stages.map((s) => {
                                return (
                                    <MenuItem key={s.id} value={s.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <Typography variant="body2">{s.name}</Typography>
                                            <Chip size="small"
                                                sx={{
                                                    fontSize: "11px",
                                                    backgroundColor: s.stage_type === "REVIEW" ? "#1976d2" : "default",
                                                    color: s.stage_type === "REVIEW" ? "#ffffff" : "#000000",
                                                    border: "1px solid #ccc",
                                                    marginLeft: "10px"
                                                }}
                                                label={s.category || "Task"}
                                            />
                                        </Box>
                                    </MenuItem>
                                )
                            })}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <Autocomplete
                            size='small'
                            multiple
                            options={statusOptions}
                            // getOptionLabel={(s) => s.toString()}
                            getOptionLabel={option => option}
                            value={status}
                            onChange={(e, v) => setStatus(v)}
                            renderInput={(params) => <TextField {...params} label="Status" />}
                            fullWidth
                        />
                    </FormControl>
                </Box>

                <Box component="fieldset" sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1, display: "flex", gap: "20px" }}>
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, fontFamily: "montserrat" }}>Range :</FormLabel>
                    <FormControl sx={{ width: 200 }} size='small'>
                        <InputLabel>Select stages</InputLabel>
                        <Select size='small' value={rangeStageId} label="Stage for Range" onChange={e => setRangeStageId(e.target.value)}>
                            <MenuItem value="Task">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <Typography variant="body2">Task</Typography>
                                    <Chip
                                        size="small"
                                        sx={{
                                            fontSize: "11px",
                                            backgroundColor: "#f0f0f0",
                                            color: "#000000",
                                            border: "1px solid #ccc",
                                        }}
                                        label="Task"
                                    />
                                </Box>
                            </MenuItem>
                            {stages.map(s => {
                                return (
                                    <MenuItem key={s.id} value={s.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <Typography variant="body2">{s.name}</Typography>
                                            <Chip
                                                size="small"
                                                sx={{
                                                    fontSize: "11px",
                                                    backgroundColor: s.stage_type === "REVIEW" ? "#1976d2" : "default",
                                                    color: s.stage_type === "REVIEW" ? "#ffffff" : "#000000",
                                                    border: "1px solid #ccc",
                                                    marginLeft: "10px"
                                                }}
                                                label={s.category || "Task"}
                                            />
                                        </Box>
                                    </MenuItem>
                                )
                            })}
                        </Select>
                    </FormControl>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileDateTimePicker
                            label="From"
                            value={from}
                            onChange={(newVal: Dayjs | null) => setFrom(newVal)}
                            format="DD-MM-YYYY HH:mm"
                            slotProps={{ textField: { size: "small", placeholder: "Start (DD-MM-YYYY HH:mm)" } as any }}
                        />
                        <EastIcon sx={{ fontSize: 20, mx: 1, marginTop: "9px" }} />

                        <MobileDateTimePicker
                            label="To"
                            value={to}
                            onChange={(newVal: Dayjs | null) => setTo(newVal)}
                            format="DD-MM-YYYY HH:mm"
                            minDateTime={from ?? undefined}
                            slotProps={{ textField: { size: "small", placeholder: "End (DD-MM-YYYY HH:mm)" } as any }}
                        />
                    </LocalizationProvider>
                </Box>

                <Box component="fieldset" sx={{ mb: 1, p: 2, border: "1px solid #e0e0e0", borderRadius: 1, display: "flex" }}>
                    <FormLabel component="legend" sx={{ fontWeight: 600, fontFamily: "montserrat" }}>Vendor :</FormLabel>
                    <FormControl margin="normal" fullWidth>
                        <VendorSearchBar
                            open={vendorSearchOpen}
                            handleOpen={() => setVendorSearchOpen(true)}
                            handleClose={() => setVendorSearchOpen(false)}
                            options={vendors}
                            selectedRow={null}
                            selectedVendor={selectedVendor}
                            setVendorId={setVendorId}
                            setSelectedVendor={setSelectedVendor}
                            loading={getVendorStatus === STATUS.PENDING}
                            handleAutocompleteInput={handleVendorSearchInput}
                        />
                    </FormControl>
                </Box>

                <Box component="fieldset" sx={{ mb: 1, p: 2, border: "1px solid #e0e0e0", borderRadius: 1, display: "flex" }}>
                    <FormLabel component="legend" sx={{ fontWeight: 600, fontFamily: "montserrat" }}>Config Data :</FormLabel>
                    <FormControl margin="normal">
                        <RadioGroup
                            row
                            value={configDataOption}
                            onChange={e => setConfigDataOption(e.target.value as any)}
                        >
                            <FormControlLabel
                                value="user_selected"
                                label={<span>{"User selected value"}</span>}
                                control={<Radio />}
                            />
                            <FormControlLabel
                                value="output_value"
                                label={<span>{"Output value"}</span>}
                                control={<Radio />}
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                <FormGroup row>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={expertData}
                                onChange={e => setExpertData(e.target.checked)}
                            />
                        }
                        label={<span>{"Expert Data Required"}</span>}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={inputTaskData}
                                onChange={e => setInputTaskData(e.target.checked)}
                            />
                        }
                        label={<span>{"Input Task Data Required"}</span>}
                    />
                </FormGroup>
                </>
                )}
            </DialogContent>

            <DialogActions>
                {/* <Button onClick={onClose}>Cancel</Button> */}
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                >
                    Download
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ExportCompletedTasksDialog
